import { useCallback, useMemo } from "react";
import { useAppStore } from "../../store/store";

export default function PortfolioInformation({
  recommendedPortfolio,
  adjustment
}) {
  const { form } = useAppStore();

  const formatDollarAmount = (number) => {
    if (number) {
      const [whole, fraction] = number.toString().split(".");

      const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      return fraction ? `${formattedWhole}.${fraction}` : formattedWhole;
    } else {
      return;
    }
  };
  /**
   * Calculates the total contributions based on the frequency and individual contributions.
   * @returns {number} The calculated total contributions.
   */
  const calcContributions = useCallback(() => {
    // Find the question related to contributions in the form's data
    let question2_5 = form.questions.find(
      (question) => question.questionId === "2.5",
    );

    if (!question2_5 || !question2_5.answerKey) {
      console.log("No contribution data found in form");
      return 0;
    }

    const answerKey = question2_5.answerKey;
    const isPercentage = answerKey.unit === "personalIntervalPercentage";

    // Log for debugging
    console.log("Contribution data in PortfolioInformation:", answerKey);

    // Get the appropriate contribution values based on whether percentages or dollars were used
    let personalContribution, employerContribution;

    if (isPercentage && answerKey.calculatedPersonalContribution) {
      // If using percentages, use the pre-calculated dollar amounts
      personalContribution =
        Number(answerKey.calculatedPersonalContribution) || 0;
      employerContribution =
        Number(answerKey.calculatedEmployerContribution) || 0;
      console.log("Using calculated dollar amounts from percentage inputs:", {
        personal: personalContribution,
        employer: employerContribution,
      });
    } else {
      // If using direct dollar amounts, use those
      personalContribution = Number(answerKey.goalsPersonalContribution) || 0;
      employerContribution = Number(answerKey.goalsEmployerContribution) || 0;
      console.log("Using direct dollar amounts:", {
        personal: personalContribution,
        employer: employerContribution,
      });
    }

    // Initialize contributions to zero
    let contributions = 0;
    const goalsFrequency = answerKey.goalsFrequency || "monthly";
    const round = (num) => Math.round(num * 1e2) / 1e2;

    // Calculate contributions based on the frequency
    switch (goalsFrequency) {
      case "monthly":
        contributions = personalContribution + employerContribution;
        break;

      case "weekly":
        contributions = round(
          (personalContribution * 52) / 12 + (employerContribution * 52) / 12,
        );
        break;

      case "bi-weekly":
        // Log the detailed calculation for bi-weekly contributions
        console.log("Bi-weekly to monthly conversion calculation:", {
          personalContribution,
          employerContribution,
          personalMonthly: (personalContribution * 26) / 12,
          employerMonthly: (employerContribution * 26) / 12,
          totalMonthly:
            (personalContribution * 26) / 12 + (employerContribution * 26) / 12,
        });
        contributions = round(
          (personalContribution * 26) / 12 + (employerContribution * 26) / 12,
        );
        break;

      case "semi-monthly":
        contributions = round(
          (personalContribution * 24) / 12 + (employerContribution * 24) / 12,
        );
        break;

      case "annually":
        contributions = round(
          personalContribution / 12 + employerContribution / 12,
        );
        break;

      default:
        break;
    }

    console.log(
      `Equivalent monthly contributions calculated: $${contributions}`,
    );
    let adjustedContribution = 0;
    if (adjustment === "raiseYourContributions") {
      adjustedContribution = 100;
    }
    return contributions + adjustedContribution;
  }, [form.questions, adjustment]);

  // Calculate total assets by combining account assets from login and outside assets from form
  const formattedAccountValue = useMemo(() => {
    // Get account assets from the user object (from login response)
    const { user } = useAppStore.getState();

    // Check for both accountAssets and accountValue to ensure compatibility
    const accountAssets = user?.accountAssets
      ? Number(user.accountAssets)
      : user?.accountValue
        ? Number(user.accountValue)
        : 0;
    console.log(
      "Account assets from login (after checking both properties):",
      accountAssets,
    );

    // Both the goals-based (2.2) and RTQ (3.2) "other retirement accounts"
    // screens save under the shared question id "outside-assets" (see
    // OtherRetirementAccounts.js's useAnswer("outside-assets")), not under
    // the screen number — looking up by screen id always missed.
    let outsideAssets = 0;
    try {
      const outsideAssetsQuestion = form.questions.find(
        (q) => q.questionId === "outside-assets",
      );

      if (
        outsideAssetsQuestion?.answerKey?.outsideAssets &&
        Array.isArray(outsideAssetsQuestion.answerKey.outsideAssets)
      ) {
        outsideAssets = outsideAssetsQuestion.answerKey.outsideAssets.reduce(
          (total, asset) => total + (Number(asset.value) || 0),
          0,
        );
      }
    } catch (error) {
      console.error("Error calculating outside assets:", error);
    }

    // Calculate total assets (account assets from login + outside assets from form)
    const totalAssets = accountAssets + outsideAssets;
    console.log(
      "Total assets calculated:",
      totalAssets,
      "(account assets:",
      accountAssets,
      "+ outside assets:",
      outsideAssets,
      ")",
    );
    return Math.floor(totalAssets).toLocaleString();
  }, [form.questions]);

  // Retrieve the retirement goal value from form data with a fallback to API value
  const formattedGoal = useMemo(() => {
    // Try to get the retirement goal from form question 2.6 first
    let retirementGoal = 0; // No default value, only use actual data

    try {
      // Look for the retirement goal in question 2.6
      const retirementGoalQuestion = form.questions.find(
        (q) => q.questionId === "2.6",
      );

      if (retirementGoalQuestion?.answerKey) {
        const parsedGoal = Number(retirementGoalQuestion.answerKey);
        if (!isNaN(parsedGoal) && parsedGoal > 0) {
          retirementGoal = parsedGoal;
          console.log("Using retirement goal from form:", retirementGoal);
        }
      }
    } catch (error) {
      console.error("Error retrieving retirement goal from form:", error);
    }

    // Use the API value if available, otherwise use what we found in the form
    const finalGoal = recommendedPortfolio?.goal || retirementGoal;

    let goalAdjustment = 1.0;
    if (adjustment === "lowerYourGoal") {
      goalAdjustment = 0.9;
    }
    console.log("Final goal value used:", finalGoal * goalAdjustment);

    return formatDollarAmount(finalGoal * goalAdjustment);
  }, [recommendedPortfolio, form.questions, adjustment]);

  const contributions = useMemo(
    () => calcContributions().toLocaleString(),
    [calcContributions],
  );

  const timeUntilRetirement = useMemo(() => {
    if (adjustment === "workLonger") {
      return recommendedPortfolio.workLonger;
    }
    return recommendedPortfolio.timeUntilRetirement;
  }, [adjustment, recommendedPortfolio]);

  return (
    <>
      <li>Total Assets: ${formattedAccountValue}</li>
      <li>Goal: ${formattedGoal}</li>
      <li>
        {} Equivalent Monthly Contributions: ${contributions}
      </li>
      <li>Time until retirement: {timeUntilRetirement}</li>
    </>
  );
}
